import React, { useState } from 'react';
import { Modal, Button, Typography, useToast, Alert, AlertDescription } from '@crisellevaldez/speculo-ui';
import { SearchableSelect } from '../../common/SearchableSelect';
import { Trash, Tag } from 'lucide-react';
import { ConfirmationDialog } from '../../common/dialogs/ConfirmationDialog';
import { useUniqueContactTags } from '../../../hooks/contacts/useUniqueContactTags';
import { useDeleteContactsByTag } from '../../../hooks/contacts/useDeleteContactsByTag';
import { useRemoveTagFromContacts } from '../../../hooks/contacts/useRemoveTagFromContacts';
import { useQueryClient } from '@tanstack/react-query';
import { useOrganizationStore } from '../../../stores/useOrganizationStore';

interface BulkDeleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const BulkDeleteModal: React.FC<BulkDeleteModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const { data: tags = [], isLoading: isLoadingTags, hasMore: tagsHasMore, loadMore: tagsLoadMore, setSearch: tagsSetSearch } = useUniqueContactTags(isOpen);
  const { deleteContacts, isLoading: isDeleting } = useDeleteContactsByTag();
  const { removeTag, isLoading: isRemoving } = useRemoveTagFromContacts();
  const { showToast } = useToast();
  const [selectedTag, setSelectedTag] = useState<string>('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [operationType, setOperationType] = useState<'delete_contacts' | 'remove_tags'>(
    'delete_contacts'
  );
  const queryClient = useQueryClient();
  const { organization } = useOrganizationStore();

  const isProcessing = isDeleting || isRemoving;

  // Function to remove the tag from the React Query cache
  const removeTagFromCache = (tag: string) => {
    // Get the current data from the cache
    const queryKey = ['uniqueContactTags', organization?.id];
    const currentTags = queryClient.getQueryData<string[]>(queryKey) || [];

    // Remove the tag from the array
    const updatedTags = currentTags.filter((t) => t !== tag);

    // Update the cache with the modified data
    queryClient.setQueryData(queryKey, updatedTags);
  };

  const handleConfirmAction = async () => {
    try {
      if (operationType === 'delete_contacts') {
        const response = await deleteContacts(selectedTag);
        // Extract the number of deleted contacts from the response
        const deletedContacts = response?.deleted_contacts || 0;

        // Remove the tag from the React Query cache
        removeTagFromCache(selectedTag);

        showToast({
          message: `Successfully deleted ${deletedContacts} contacts with tag "${selectedTag}"`,
          variant: 'success',
        });
      } else {
        const response = await removeTag(selectedTag);
        // Extract the number of updated contacts from the response
        const updatedContacts = response?.updated_contacts || 0;

        // Remove the tag from the React Query cache
        removeTagFromCache(selectedTag);

        showToast({
          message: `Successfully removed tag "${selectedTag}" from ${updatedContacts} contacts`,
          variant: 'success',
        });
      }

      // Refresh the contacts list
      onSuccess?.();

      // Reset the selected tag
      setSelectedTag('');
      setShowConfirmation(false);
      onClose();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      showToast({
        message: `Error ${operationType === 'delete_contacts' ? 'deleting contacts' : 'removing tag'}: ${errorMessage}`,
        variant: 'error',
      });
      setShowConfirmation(false);
    }
  };

  const handleCancel = () => {
    setSelectedTag('');
    onClose();
  };

  const handleCloseModal = () => {
    setSelectedTag('');
    onClose();
  };

  const tagOptions = (tags || []).map((tag) => ({ value: tag, label: tag }));

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="sm">
        <Modal.Header>
          <div className="flex items-center gap-3">
            <Typography variant="h4" className="font-bold">
              {operationType === 'delete_contacts' ? 'Bulk Delete by Tag' : 'Bulk Remove Tag'}
            </Typography>
          </div>
        </Modal.Header>
        <Modal.Body>
          <div className="space-y-4">
            <div className="flex flex-col gap-2">
              <Typography variant="small" className="font-medium">
                Select operation:
              </Typography>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={operationType === 'delete_contacts'}
                    onChange={() => setOperationType('delete_contacts')}
                  />
                  <span className="text-sm">Delete all contacts with tag</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={operationType === 'remove_tags'}
                    onChange={() => setOperationType('remove_tags')}
                  />
                  <span className="text-sm">Remove tag from all contacts</span>
                </label>
              </div>
            </div>

            <Alert variant={operationType === 'delete_contacts' ? 'warning' : 'info'}>
              <AlertDescription>
                {operationType === 'delete_contacts'
                  ? 'Select a tag to delete all contacts associated with it. This action cannot be undone.'
                  : 'Select a tag to remove it from all contacts that have it. Contacts will be preserved.'}
              </AlertDescription>
            </Alert>
            <div className="pt-2">
              <SearchableSelect
                options={tagOptions}
                value={selectedTag}
                onChange={(value) => setSelectedTag(value)}
                placeholder={isLoadingTags ? 'Loading tags...' : 'Select a tag'}
                className="w-full"
                isLoading={isLoadingTags}
                hasMore={tagsHasMore}
                onLoadMore={tagsLoadMore}
                onSearch={tagsSetSearch}
                searchPlaceholder="Search tags..."
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={handleCloseModal} size="sm" disabled={isProcessing}>
              Cancel
            </Button>
            <Button
              onClick={() => setShowConfirmation(true)}
              size="sm"
              className={
                operationType === 'delete_contacts'
                  ? 'bg-red-700 hover:bg-red-600 text-white flex items-center gap-2'
                  : 'bg-amber-600 hover:bg-amber-500 text-white flex items-center gap-2'
              }
              disabled={!selectedTag || isProcessing}
              isLoading={isProcessing}
            >
              {operationType === 'delete_contacts' ? (
                <>
                  <Trash className="w-4 h-4" />
                  Confirm Delete
                </>
              ) : (
                <>
                  <Tag className="w-4 h-4" />
                  Confirm Remove Tag
                </>
              )}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <ConfirmationDialog
        isOpen={showConfirmation}
        onClose={() => !isProcessing && setShowConfirmation(false)}
        onConfirm={handleConfirmAction}
        title={
          operationType === 'delete_contacts' ? 'Confirm Bulk Delete' : 'Confirm Bulk Tag Removal'
        }
        message={
          operationType === 'delete_contacts'
            ? `Are you sure you want to delete ALL contacts with the tag "${selectedTag}"? This action is irreversible and will permanently remove these contacts from your database. The tag will also be removed from the system.`
            : `Are you sure you want to remove the tag "${selectedTag}" from ALL contacts that have it? The contacts will remain in your database, but this tag will be removed from all contacts and from the system.`
        }
        confirmLabel={operationType === 'delete_contacts' ? 'Delete Contacts' : 'Remove Tag'}
        variant={operationType === 'delete_contacts' ? 'danger' : 'warning'}
        isLoading={isProcessing}
      />
    </>
  );
};

export default BulkDeleteModal;
